// Copyright 2024 Vajra. All rights reserved.
// API Client

import axios from 'axios';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem('vajra-auth');
        if (refreshToken) {
          const parsed = JSON.parse(refreshToken);
          const response = await api.post('/auth/refresh', {
            refreshToken: parsed.state?.refreshToken,
          });

          const { accessToken } = response.data;
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;

          // Update stored token
          parsed.state.accessToken = accessToken;
          localStorage.setItem('vajra-auth', JSON.stringify(parsed));

          return api(originalRequest);
        }
      } catch {
        // Refresh failed, redirect to login
        localStorage.removeItem('vajra-auth');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// API helper functions
export const apiClient = {
  // Auth
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),

  // Policies
  getPolicies: () => api.get('/policies'),
  getPolicy: (id: string) => api.get(`/policies/${id}`),
  createPolicy: (data: unknown) => api.post('/policies', data),
  updatePolicy: (id: string, data: unknown) => api.put(`/policies/${id}`, data),
  deletePolicy: (id: string) => api.delete(`/policies/${id}`),

  // Devices
  getDevices: (params?: Record<string, string>) =>
    api.get('/devices', { params }),
  getDevice: (id: string) => api.get(`/devices/${id}`),
  deleteDevice: (id: string) => api.delete(`/devices/${id}`),

  // Users
  getUsers: (params?: Record<string, string>) =>
    api.get('/users', { params }),
  getUser: (id: string) => api.get(`/users/${id}`),
  createUser: (data: unknown) => api.post('/users', data),
  updateUser: (id: string, data: unknown) => api.patch(`/users/${id}`, data),
  deleteUser: (id: string) => api.delete(`/users/${id}`),

  // Telemetry
  getEvents: (params?: Record<string, string>) =>
    api.get('/telemetry/events', { params }),
  getDashboardMetrics: () => api.get('/telemetry/metrics/dashboard'),
  getSecurityMetrics: () => api.get('/telemetry/metrics/security'),

  // Reports
  getReports: () => api.get('/reports'),
  getReport: (id: string) => api.get(`/reports/${id}`),
  createReport: (data: unknown) => api.post('/reports', data),
  generateReport: (id: string) => api.post(`/reports/${id}/generate`),

  // Settings
  getSettings: () => api.get('/settings'),
  updateSettings: (category: string, data: unknown) =>
    api.patch(`/settings/${category}`, data),

  // Enrollment Tokens
  getEnrollmentTokens: () => api.get('/settings/enrollment/tokens'),
  createEnrollmentToken: (data: { name: string; expiresAt?: string; maxUsage?: number }) =>
    api.post('/settings/enrollment/tokens', data),
  revokeEnrollmentToken: (id: string) => api.delete(`/settings/enrollment/tokens/${id}`),

  // Samyoga Integration
  samyoga: {
    // RAG Chat
    ragQuery: (query: string) =>
      api.post('/samyoga/rag/query', { query }),
    ragSuggestions: (context?: string) =>
      api.get('/samyoga/rag/suggestions', { params: { context } }),

    // Threat Intelligence
    lookupThreat: (ioc: string, type: string) =>
      api.get('/samyoga/threat-intel/lookup', { params: { ioc, type } }),
    lookupThreatBatch: (iocs: Array<{ ioc: string; type: string }>) =>
      api.post('/samyoga/threat-intel/lookup/batch', { iocs }),

    // AI-SPM
    checkPrompt: (prompt: string, service?: string) =>
      api.post('/samyoga/ai-spm/prompt-check', { prompt, service }),
    getAIServices: () =>
      api.get('/samyoga/ai-spm/services'),

    // M365 Security
    getM365Risk: (tenantId: string) =>
      api.get(`/samyoga/m365/risk/${tenantId}`),
    getM365Recommendations: (tenantId: string) =>
      api.get(`/samyoga/m365/recommendations/${tenantId}`),

    // User Risk
    getUserRisk: (userId: string) =>
      api.get(`/samyoga/risk/user/${userId}`),

    // Policy Updates (webhook receiver)
    processPolicyUpdate: (payload: unknown) =>
      api.post('/samyoga/policy/update', payload),
  },
};

export default api;
